// Фабрика транспорта: сводит REST/WS/Outbox/Push/events в один Transport. SPEC client-transport §4.1.
//
// Один construct на приложение. Браузерные API инъектируются как порты (fetchImpl/wsFactory/storage/
// network/uuid/now) — тесты подставляют фейки. Публичная поверхность структурно совместима с
// client-store TransportPort (events()/rest/socket/outbox/newClientMsgId) — потребитель не правится.
import { TransportError } from './errors.js';
import { EventHub } from './event-hub.js';
import { HttpEngine } from './http.js';
import { createBrowserNetworkMonitor, createBrowserVisibilityMonitor } from './network.js';
import { OutboxQueue } from './outbox.js';
import { createIndexedDbOutboxStore } from './outbox-store.js';
import { createPushClient, createRestClient } from './rest-client.js';
import { DEFAULT_CONNECT_TIMEOUT_MS, WsRealtimeClient } from './realtime-client.js';
import { createRestPoller } from './rest-poller.js';
import { DEFAULT_BACKOFF, } from './ports.js';
// ─────────────────────────────────────────────────────────────────────────────
// Фабрика. SPEC §4.1.
// ─────────────────────────────────────────────────────────────────────────────
/** Выводит WSS-URL из baseUrl + wsPath: https→wss, http→ws. */
export function deriveWsUrl(baseUrl, wsPath) {
    const trimmed = baseUrl.replace(/\/+$/, '');
    const path = wsPath.startsWith('/') ? wsPath : `/${wsPath}`;
    if (trimmed.startsWith('https://'))
        return `wss://${trimmed.slice('https://'.length)}${path}`;
    if (trimmed.startsWith('http://'))
        return `ws://${trimmed.slice('http://'.length)}${path}`;
    // baseUrl без схемы — считаем защищённым (wss).
    return `wss://${trimmed}${path}`;
}
function defaultUuid() {
    return () => {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        // Фолбэк (среды без crypto.randomUUID) — RFC 4122 v4 на Math.random (только для не-крипто id).
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    };
}
export function createTransport(opts) {
    const wsPath = opts.wsPath ?? '/ws';
    // Дефолтный fetch БИНДИМ к globalThis: HttpEngine хранит ссылку в `this.fetchImpl` и зовёт её как
    // МЕТОД (`this.fetchImpl(...)`), из-за чего нативный fetch получил бы `this = HttpEngine` вместо
    // Window → `TypeError: Illegal invocation` на КАЖДОМ REST-вызове (в браузере; юнит-тесты инъектят
    // свой fetchImpl, поэтому дефолт не покрыт). Инъецированный fetchImpl используем как есть.
    const fetchImpl = opts.fetchImpl ??
        (typeof globalThis.fetch === 'function'
            ? globalThis.fetch.bind(globalThis)
            : undefined);
    if (typeof fetchImpl !== 'function') {
        throw new TransportError('INTERNAL', 'fetch недоступен: передайте fetchImpl в createTransport');
    }
    const wsFactory = opts.wsFactory ??
        ((url, protocols) => new WebSocket(url, protocols));
    const store = opts.storage ?? createIndexedDbOutboxStore();
    const network = opts.network ?? createBrowserNetworkMonitor();
    const visibility = opts.visibility ?? createBrowserVisibilityMonitor();
    const now = opts.now ?? (() => Date.now());
    const rand = opts.rand ?? Math.random;
    const uuid = opts.uuid ?? defaultUuid();
    const backoff = { ...DEFAULT_BACKOFF, ...opts.backoff };
    const setTimeoutFn = opts.setTimeoutFn ?? ((fn, ms) => setTimeout(fn, ms));
    const clearTimeoutFn = opts.clearTimeoutFn ?? ((h) => clearTimeout(h));
    const hub = new EventHub();
    // ── auth.required дебаунс (§4.9): эмитим один раз, пока не было успешного восстановления ──
    let authRequiredEmitted = false;
    const emitAuthRequired = () => {
        if (authRequiredEmitted)
            return;
        authRequiredEmitted = true;
        hub.emit({ t: 'auth.required' });
    };
    // ── HTTP / REST ──────────────────────────────────────────────────────────────
    const http = new HttpEngine({
        baseUrl: opts.baseUrl,
        fetchImpl,
        csrfSource: opts.csrfSource,
        onUnauthenticated: emitAuthRequired,
        defaultTimeoutMs: opts.requestTimeoutMs,
        xhrFactory: opts.xhrFactory ??
            (typeof XMLHttpRequest === 'function' ? () => new XMLHttpRequest() : undefined),
    });
    const rest = createRestClient(http);
    const push = createPushClient(rest);
    // ── WS realtime ────────────────────────────────────────────────────────────────
    const socket = new WsRealtimeClient({
        url: deriveWsUrl(opts.baseUrl, wsPath),
        ...(opts.wsFallbackUrls !== undefined && opts.wsFallbackUrls.length > 0
            ? {
                fallbackUrls: opts.wsFallbackUrls,
                // тикет добывается по REST primary-origin (cookie); 401 внутри штатно эмитит auth.required
                getWsTicket: async () => {
                    try {
                        const r = await rest.wsTicket();
                        return r.ticket;
                    }
                    catch {
                        return null;
                    }
                },
            }
            : {}),
        wsFactory,
        backoff,
        network,
        visibility,
        connectTimeoutMs: opts.wsConnectTimeoutMs ?? DEFAULT_CONNECT_TIMEOUT_MS,
        now,
        rand,
        setTimeoutFn,
        clearTimeoutFn,
        onServerEvent: (event) => {
            hub.emit({ t: 'server', event });
            // ack-путь (§4.3.7): сводим серверное подтверждение с очередью по clientMsgId.
            if (event.t === 'ack') {
                void outbox.onAck(event.clientMsgId);
            }
        },
        onStatus: (status) => {
            // Восстановление после auth.required: открытый сокет снимает дебаунс (новая cookie сработала).
            if (status === 'open')
                authRequiredEmitted = false;
            hub.emit({ t: 'conn', status });
        },
        onProtocolOutdated: (serverV, clientV) => {
            hub.emit({ t: 'protocol.outdated', serverV, clientV });
        },
        onAuthRequired: emitAuthRequired,
        onReady: () => {
            // reconnect → дослать очередь (§4.6.4).
            void outbox.flush();
        },
    });
    // ── Outbox ──────────────────────────────────────────────────────────────────────
    const outbox = new OutboxQueue({
        store,
        sender: { sendMessage: (chatId, dto) => rest.sendMessage(chatId, dto) },
        // Отправка идёт по REST и НЕ требует живого WS (client-diag 2026-07-05: провайдер душит
        // WS-хендшейки при идеальном HTTP — замок на socket.isReady() держал очередь без причины).
        // Стоп только на офлайне и невалидной сессии (auth.required → ждём релогин).
        canSend: () => network.isOnline() && !authRequiredEmitted,
        now,
        onChanged: (item) => hub.emit({ t: 'outbox.changed', item }),
        onSent: (_item, message) => {
            // Серверный Message пришёл по REST-ответу. Он же прилетит как WS message.new/ack — наружу его
            // НЕ дублируем тут (стор сводит оптимистичное с серверным по clientMsgId через ack/message.new).
            void message;
        },
    });
    // online → дослать очередь (§4.6.4). socket сам поднимет соединение; flush сработает по ready,
    // но и прямой online-триггер ускоряет дослыл, если сокет уже был open.
    network.onOnline(() => {
        void outbox.flush();
    });
    // ── REST-поллер деградированного приёма (WS лежит, HTTP жив — client-diag 2026-07-05) ────────
    // Курсоры зеркалим на пути client-store → socket.setCursors (стор двигает их на росте maxSeq).
    let cursorsMirror = [];
    const socketFacade = {
        get status() {
            return socket.status;
        },
        setCursors: (cursors) => {
            cursorsMirror = [...cursors];
            socket.setCursors(cursors);
        },
        send: (cmd) => socket.send(cmd),
        close: () => socket.close(),
    };
    const pollIntervalMs = opts.pollIntervalMs ?? 15_000;
    const poller = createRestPoller({
        rest,
        network,
        isWsReady: () => socket.isReady(),
        isAuthBlocked: () => authRequiredEmitted,
        isVisible: () => visibility.isVisible(),
        getCursors: () => cursorsMirror,
        emit: (event) => hub.emit({ t: 'server', event }),
        onTick: () => void outbox.flush(), // HTTP жив — дослать застрявшую очередь
        intervalMs: pollIntervalMs,
        setIntervalFn: (fn, ms) => {
            // переиспользуем инъекцию таймеров транспорта (ManualTimers в тестах); интервал строим на
            // цепочке setTimeout — у TransportOptions нет отдельного setIntervalFn.
            let cancelled = false;
            const tickWrap = () => {
                if (cancelled)
                    return;
                fn();
                handleRef.handle = setTimeoutFn(tickWrap, ms);
            };
            const handleRef = {
                handle: setTimeoutFn(tickWrap, ms),
                cancel() {
                    cancelled = true;
                    clearTimeoutFn(this.handle);
                },
            };
            return handleRef;
        },
        clearIntervalFn: (h) => h.cancel(),
    });
    return {
        rest,
        socket: socketFacade,
        outbox,
        push,
        events: () => hub.events(),
        async start() {
            await outbox.restore();
            socket.start();
            if (pollIntervalMs > 0)
                poller.start();
        },
        async stop() {
            poller.stop();
            socket.stop();
            hub.close();
        },
        newClientMsgId() {
            return uuid();
        },
    };
}
//# sourceMappingURL=transport.js.map